// ./src/components/layouts/DashboardLayout/MoreView.tsx
// Full-screen "More" menu view for mobile devices.
// Replaces the mobile sidebar with a native-feeling settings/menu screen.
// Shows remaining navigation items (not in bottom tab bar),
// account menu items, and sign out.

import React, { useMemo, useCallback, useState } from "react";
import {
  Box,
  Group,
  Text,
  Stack,
  Paper,
  Avatar,
  ScrollArea,
  UnstyledButton,
  Divider,
  Loader,
  TextInput,
  CloseButton,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useBranding } from "@/contexts/BrandingContext";

import { SiteMapItem, preloadRoute, splitShowMore, activeNavItem } from "@/config";

import { User } from "@/utils/api/UserService";

import { AppIcon } from "@/components/generic/AppIcon";

import { DEFAULT_ICON_COLOR, getIconBgColor } from "./constants";

// ============================================
// Types
// ============================================

export interface MoreViewProps {
  /** Navigation items to display (already sliced — excludes bottom tab items) */
  navigationItems: SiteMapItem[];

  /** Current user */
  user: User | null | undefined;

  /** Whether user is account owner */

  /** App version string */
  appVersion: string;

  /** Current route path — used to detect same-path taps */
  currentPath: string;

  /** Navigation handler */
  onNavigate: (path: string) => void;

  /** Close the More view without navigating (e.g. same-path tap) */
  onClose: () => void;

  /** Sign out handler */
  onSignOut: () => void;

  /** Translation helper for nav item names */
  getTranslatedName: (translationKey?: string, fallback?: string) => string;

  /** Translation helper for nav item subtitles */
  getTranslatedSubtitle: (translationKey?: string, fallback?: string) => string;
}

// ============================================
// Constants
// ============================================

const ICON_CIRCLE_SIZE = 40;
const ICON_SIZE = 20;

/** Account menu items — derived from sitemap hidden items */
// ⚠ Profile only. Account Settings and Subscription are reached through the nav sections this
// screen already renders — see the note in the account block below.
const ACCOUNT_PATHS = {
  profile: "/settings/profile",
};

// ============================================
// CSS class names for tap animation
// ============================================

const TAP_STYLES = `
  .more-menu-item:active {
    transform: scale(0.97);
  }
  .more-menu-item-navigating {
    animation: more-menu-pulse 1s ease-in-out infinite;
  }
  @keyframes more-menu-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

// ============================================
// Sub-components
// ============================================

interface MenuItemRowProps {
  iconName: string;
  iconColor: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
  /** Starts the destination's chunk as the finger lands — see BottomTabBar's note. */
  onPointerDown?: () => void;
  /** This item was tapped and navigation is in progress */
  isNavigating?: boolean;
  /** Another item is navigating — dim this one */
  isOtherNavigating?: boolean;
  /** ROADMAP #63 — the account's plan does not include this page: a crown, beside the name. */
  premium?: boolean;
  /** Accessible name of the premium mark. */
  premiumLabel?: string;
  /** A toggle row ("Show more"), not a destination: no chevron. */
  isToggle?: boolean;
}

const MenuItemRow: React.FC<MenuItemRowProps> = ({
  iconName,
  iconColor,
  title,
  subtitle,
  onClick,
  onPointerDown,
  isNavigating = false,
  isOtherNavigating = false,
  premium = false,
  premiumLabel,
  isToggle = false,
}) => {
  const theme = useMantineTheme();

  return (
    <UnstyledButton
      onClick={onClick}
      onPointerDown={onPointerDown}
      w="100%"
      disabled={isNavigating || isOtherNavigating}
      className={`more-menu-item ${isNavigating ? "more-menu-item-navigating" : ""}`}
      style={{
        WebkitTapHighlightColor: "transparent",
        opacity: isOtherNavigating ? 0.5 : 1,
        transition: "opacity 150ms ease, transform 100ms ease",
      }}
    >
      <Group
        gap="sm"
        wrap="nowrap"
        px="md"
        py="sm"
        bg={isNavigating ? "brand.0" : undefined}
        style={{
          borderRadius: theme.radius.sm,
          transition: "background-color 150ms ease",
        }}
      >
        {/* Icon Circle */}
        <Box
          w={ICON_CIRCLE_SIZE}
          h={ICON_CIRCLE_SIZE}
          style={{
            backgroundColor: getIconBgColor(iconColor),
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AppIcon iconName={iconName} size={ICON_SIZE} color={iconColor} />
        </Box>

        {/* Text Content */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text
            fw={600}
            size="md"
            c={isNavigating || isToggle ? "brand.7" : "neutral.8"}
            lineClamp={1}
            style={{ transition: "color 150ms ease" }}
          >
            {title}
          </Text>
          {subtitle && (
            <Text size="md" c="neutral.5" lineClamp={1}>
              {subtitle}
            </Text>
          )}
        </Box>

        {premium ? (
          <Box component="span" role="img" aria-label={premiumLabel} style={{ display: "flex", flexShrink: 0 }}>
            <AppIcon iconName="Crown" size={16} color={theme.colors.yellow[6]} />
          </Box>
        ) : null}

        {/* Chevron or Loading Indicator */}
        {isToggle ? null : isNavigating ? (
          <Loader size={16} color={theme.colors.brand[6]} type="dots" />
        ) : (
          <AppIcon
            iconName="ChevronRight"
            size={18}
            color={theme.colors.neutral[4]}
          />
        )}
      </Group>
    </UnstyledButton>
  );
};

interface SectionHeaderProps {
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => {
  return (
    <Text
      size="md"
      fw={700}
      c="neutral.5"
      tt="uppercase"
      px="md"
      pt="lg"
      pb="xs"
      style={{ letterSpacing: "0.5px" }}
    >
      {title}
    </Text>
  );
};

interface UserCardProps {
  user: User;
  onNavigate: (path: string) => void;
  isNavigating?: boolean;
  isOtherNavigating?: boolean;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onNavigate,
  isNavigating = false,
  isOtherNavigating = false,
}) => {
  const theme = useMantineTheme();

  const userInitials =
    user.username
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";

  return (
    <UnstyledButton
      onClick={() => onNavigate(ACCOUNT_PATHS.profile)}
      w="100%"
      px="md"
      disabled={isNavigating || isOtherNavigating}
      className={`more-menu-item ${isNavigating ? "more-menu-item-navigating" : ""}`}
      style={{
        WebkitTapHighlightColor: "transparent",
        opacity: isOtherNavigating ? 0.5 : 1,
        transition: "opacity 150ms ease, transform 100ms ease",
      }}
    >
      <Paper
        withBorder
        p="md"
        radius="md"
        bg={isNavigating ? "brand.0" : undefined}
        style={{ transition: "background-color 150ms ease" }}
      >
        <Group gap="sm" wrap="nowrap">
          <Avatar size={44} color="brand" radius="md">
            {userInitials}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} size="md" c="neutral.8" truncate>
              {user.username}
            </Text>
            <Text size="md" c="neutral.5" truncate>
              {user.email}
            </Text>
          </Box>
          {isNavigating ? (
            <Loader size={16} color={theme.colors.brand[6]} type="dots" />
          ) : (
            <AppIcon
              iconName="ChevronRight"
              size={18}
              color={theme.colors.neutral[4]}
            />
          )}
        </Group>
      </Paper>
    </UnstyledButton>
  );
};

// ============================================
// Main Component
// ============================================

export const MoreView: React.FC<MoreViewProps> = ({
  navigationItems,
  user,
  appVersion,
  currentPath,
  onNavigate,
  onClose,
  onSignOut,
  getTranslatedName,
  getTranslatedSubtitle,
}) => {
  const { t } = useTranslation();
  // A2e #101 — the brand from the white label, never a name written here.
  const { brand } = useBranding();
  const theme = useMantineTheme();

  // Track which path is currently navigating
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);

  // Page search — filters the whole More list (pages + account items) by name.
  const [query, setQuery] = useState("");

  /**
   * ROADMAP A2b #63 — which "Show more" rows are open, per section (and "__menu" for the sections an event does not
   * use at all). ⓘ Local: this screen is opened for one tap and closed again, so remembering it would only surprise.
   * ⚠ While SEARCHING everything is listed — a host who typed "budget" is asking for the page, used or not.
   */
  const [openMore, setOpenMore] = useState<Record<string, boolean>>({});
  const toggleMore = useCallback((id: string) => setOpenMore((prev) => ({ ...prev, [id]: !prev[id] })), []);
  const q = query.trim().toLowerCase();
  const matches = useCallback((text?: string | null) => !q || (text || "").toLowerCase().includes(q), [q]);

  // Separate standalone items from parent groups
  const { standaloneItems, parentGroups } = useMemo(() => {
    const standalone: SiteMapItem[] = [];
    const parents: SiteMapItem[] = [];

    navigationItems.forEach((item) => {
      const hasVisibleChildren =
        Array.isArray(item.items) &&
        item.items.some((child) => child.visible !== false);

      if (hasVisibleChildren) {
        parents.push(item);
      } else if (item.url) {
        standalone.push(item);
      }
    });

    return { standaloneItems: standalone, parentGroups: parents };
  }, [navigationItems]);

  // Account items — filtered by role
  const accountItems = useMemo(() => {
    const items: Array<{ icon: string; iconColor: string; titleKey: string; subtitleKey: string; path: string }> = [
      {
        icon: "User",
        iconColor: theme.colors.neutral[6],
        titleKey: "pages.userProfilePage.name",
        subtitleKey: "pages.userProfilePage.subtitle",
        path: ACCOUNT_PATHS.profile,
      },
    ];

    // ⚠⚠ ACCOUNT SETTINGS AND SUBSCRIPTION ARE NOT LISTED HERE, DELIBERATELY. They were — and this
    // screen ALSO renders the nav sections above, which contain Settings → Account Settings and
    // Money → Subscription. So a phone showed each of them TWICE on one scroll, with different
    // icons and subtitles, reading like two different pages.
    // ▶ Same rule as the sidebar's avatar menu and the account sheet: this block is the PERSON
    // (profile, sign out); the sections above are the account. Guarded by `npm run check-nav`.
    return items;
  }, [theme]);

  // Navigation handler — if tapping the already-active page, just close More
  const handleNavigate = useCallback(
    (path: string) => {
      if (navigatingTo) return; // Prevent double-tap

      // Special paths like "__support__" are never same-path
      if (path !== "__support__" && path === currentPath) {
        // Already on this page — just close the More view
        onClose();
        return;
      }

      setNavigatingTo(path);
      onNavigate(path);
    },
    [onNavigate, onClose, navigatingTo, currentPath]
  );

  // Render a single navigation item row
  const renderNavItem = useCallback(
    (item: SiteMapItem) => {
      const itemName = getTranslatedName(item.options?.translationKey, item.name);
      const itemSubtitle = getTranslatedSubtitle(item.options?.translationKey, item.subtitle);
      const iconColor = item.iconColor || DEFAULT_ICON_COLOR;
      const itemPath = item.url || "";

      return (
        <MenuItemRow
          key={item.url || item.name}
          iconName={item.icon || "Circle"}
          iconColor={iconColor}
          title={itemName}
          subtitle={itemSubtitle}
          onClick={() => item.url && handleNavigate(item.url)}
          // Phone surface: start the chunk as the finger lands — see BottomTabBar's note.
          onPointerDown={() => preloadRoute(item)}
          isNavigating={navigatingTo === itemPath}
          isOtherNavigating={navigatingTo != null && navigatingTo !== itemPath}
          premium={!!item.navPremium}
          premiumLabel={t("navigation.premium")}
        />
      );
    },
    [getTranslatedName, getTranslatedSubtitle, handleNavigate, navigatingTo, t]
  );

  /**
   * The "Show more (n)" / "Show less" row. ⚠ In the BRAND colour, never a page row's grey — the owner could not find
   * "Show less" among identical-looking rows (2026-09-29). Open, it moves to the END of what it revealed.
   */
  const renderShowMore = useCallback(
    (id: string, count: number) => (
      <MenuItemRow
        key={`more-${id}`}
        iconName={openMore[id] ? "ChevronUp" : "ChevronDown"}
        iconColor={theme.colors.brand[6]}
        title={openMore[id] ? t("navigation.showLess") : `${t("navigation.showMore")} (${count})`}
        onClick={() => toggleMore(id)}
        isToggle
      />
    ),
    [openMore, theme, t, toggleMore]
  );

  /**
   * A section's rows: what the event uses, then "Show more", then — open — the rest.
   * ⚠ The page the host is on is never tucked away: closed, the row still lists it.
   */
  const sectionRows = useCallback(
    (parent: SiteMapItem): React.ReactNode[] => {
      const children = (parent.items || []).filter(
        (child) => child.visible !== false && matches(getTranslatedName(child.options?.translationKey, child.name))
      );

      if (q) {
        return children.map(renderNavItem);
      }

      const { shown, more } = splitShowMore(children);

      if (more.length === 0) {
        return shown.map(renderNavItem);
      }

      const id = parent.key ?? parent.name;

      if (openMore[id]) {
        return [...shown.map(renderNavItem), ...more.map(renderNavItem), renderShowMore(id, more.length)];
      }

      const active = activeNavItem(parent.items || [], currentPath);
      const revealed = more.filter((child) => child === active);

      return [...shown.map(renderNavItem), renderShowMore(id, more.length), ...revealed.map(renderNavItem)];
    },
    [q, matches, getTranslatedName, renderNavItem, renderShowMore, openMore, currentPath]
  );

  /** One section: its header and a card of its rows. */
  const renderSection = (parent: SiteMapItem) => {
    const rows = sectionRows(parent);

    if (rows.length === 0) {
      return null;
    }

    return (
      <React.Fragment key={parent.name}>
        <SectionHeader title={getTranslatedName(parent.options?.translationKey, parent.name)} />
        <Paper withBorder mx="md" radius="md" py="xs">
          {rows.map((row, index) => (
            <React.Fragment key={index}>
              {row}
              {index < rows.length - 1 && <Divider mx="md" color="neutral.1" />}
            </React.Fragment>
          ))}
        </Paper>
      </React.Fragment>
    );
  };

  /**
   * ROADMAP #63 — sections (and top-level pages) the event does not use at all sit behind ONE "Show more" at the end,
   * rather than as a column of headings each holding nothing but its own "Show more". Listed as usual while searching,
   * and the one the host is on is always listed.
   */
  const activeTop = activeNavItem(navigationItems, currentPath);
  const inMenu = (item: SiteMapItem) => !!q || !item.navMore || item === activeTop;
  const moreTop = q ? [] : navigationItems.filter((item) => item.navMore && item !== activeTop);

  return (
    <>
      {/* Inject tap animation styles */}
      <style>{TAP_STYLES}</style>

      <ScrollArea
        h="100%"
        scrollbarSize={6}
        style={{ backgroundColor: theme.colors.neutral[0] }}
      >
        <Stack gap={0} pb={100}>
          {/* Page search */}
          <Box px="md" pt="md">
            <TextInput
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder={t("navigation.searchPages", { defaultValue: "Search pages" })}
              leftSection={<AppIcon iconName="Search" size={18} />}
              rightSection={
                query ? (
                  <CloseButton
                    aria-label={t("common:form.clearSearch", { defaultValue: "Clear search" })}
                    onClick={() => setQuery("")}
                  />
                ) : undefined
              }
            />
          </Box>

          {/* User Card — hidden while searching */}
          {user && !q && (
            <Box pt="md" pb="xs">
              <UserCard
                user={user}
                onNavigate={handleNavigate}
                isNavigating={navigatingTo === ACCOUNT_PATHS.profile}
                isOtherNavigating={navigatingTo != null && navigatingTo !== ACCOUNT_PATHS.profile}
              />
            </Box>
          )}

          {/* Standalone Navigation Items */}
          {(() => {
            const items = standaloneItems.filter(
              (item) => inMenu(item) && matches(getTranslatedName(item.options?.translationKey, item.name))
            );
            if (items.length === 0) return null;

            return (
              <Paper withBorder mx="md" mt="md" radius="md" py="xs">
                {items.map((item, index) => (
                  <React.Fragment key={item.url || item.name}>
                    {renderNavItem(item)}
                    {index < items.length - 1 && <Divider mx="md" color="neutral.1" />}
                  </React.Fragment>
                ))}
              </Paper>
            );
          })()}

          {/* Parent Groups (Reports, Settings, Team) */}
          {parentGroups.filter(inMenu).map(renderSection)}

          {/* ROADMAP #63 — what this event does not use, behind one "Show more" */}
          {moreTop.length > 0 && !openMore.__menu && (
            <Paper withBorder mx="md" mt="md" radius="md" py="xs">
              {renderShowMore("__menu", moreTop.length)}
            </Paper>
          )}
          {moreTop.length > 0 && (
            <>
              {openMore.__menu && (
                <>
                  {moreTop.filter((item) => !parentGroups.includes(item)).length > 0 && (
                    <Paper withBorder mx="md" mt="md" radius="md" py="xs">
                      {moreTop
                        .filter((item) => !parentGroups.includes(item))
                        .map((item, index, list) => (
                          <React.Fragment key={item.url || item.name}>
                            {renderNavItem(item)}
                            {index < list.length - 1 && <Divider mx="md" color="neutral.1" />}
                          </React.Fragment>
                        ))}
                    </Paper>
                  )}
                  {moreTop.filter((item) => parentGroups.includes(item)).map(renderSection)}
                  <Paper withBorder mx="md" mt="md" radius="md" py="xs">
                    {renderShowMore("__menu", moreTop.length)}
                  </Paper>
                </>
              )}
            </>
          )}

          {/* Account Section */}
          {(() => {
            const items = accountItems.filter((item) => matches(t(item.titleKey)));
            if (items.length === 0) return null;

            return (
              <>
                <SectionHeader title={t("pages.account.name", { defaultValue: "Account" })} />
                <Paper withBorder mx="md" radius="md" py="xs">
                  {items.map((item, index) => (
                    <React.Fragment key={item.path}>
                      <MenuItemRow
                        iconName={item.icon}
                        iconColor={item.iconColor}
                        title={t(item.titleKey)}
                        subtitle={t(item.subtitleKey, { defaultValue: "" })}
                        onClick={() => handleNavigate(item.path)}
                        isNavigating={navigatingTo === item.path}
                        isOtherNavigating={navigatingTo != null && navigatingTo !== item.path}
                      />
                      {index < items.length - 1 && <Divider mx="md" color="neutral.1" />}
                    </React.Fragment>
                  ))}
                </Paper>
              </>
            );
          })()}

          {/* Help & Support */}
          {matches(t("navigation.helpAndSupport")) && (
            <>
              <SectionHeader title={t("navigation.support", { defaultValue: "Support" })} />
              <Paper withBorder mx="md" radius="md" py="xs">
                <MenuItemRow
                  iconName="Help"
                  iconColor={theme.colors.brand[6]}
                  title={t("navigation.helpAndSupport")}
                  subtitle=""
                  onClick={() => handleNavigate("__support__")}
                  isNavigating={navigatingTo === "__support__"}
                  isOtherNavigating={navigatingTo != null && navigatingTo !== "__support__"}
                />
              </Paper>
            </>
          )}

          {/* No matches */}
          {q &&
            standaloneItems.filter((i) => matches(getTranslatedName(i.options?.translationKey, i.name))).length === 0 &&
            parentGroups.every(
              (p) =>
                (p.items || []).filter(
                  (c) => c.visible !== false && matches(getTranslatedName(c.options?.translationKey, c.name))
                ).length === 0
            ) &&
            accountItems.filter((i) => matches(t(i.titleKey))).length === 0 &&
            !matches(t("navigation.helpAndSupport")) && (
              <Box px="md" py="xl">
                <Text c="dimmed" ta="center" size="md">
                  {t("navigation.noPagesFound", { defaultValue: "No pages found" })}
                </Text>
              </Box>
            )}

          {/* Sign Out — hidden while searching */}
          {!q && (
          <Box px="md" pt="lg">
            <UnstyledButton
              onClick={onSignOut}
              w="100%"
              disabled={navigatingTo != null}
              className="more-menu-item"
              style={{
                WebkitTapHighlightColor: "transparent",
                opacity: navigatingTo != null ? 0.5 : 1,
                transition: "opacity 150ms ease, transform 100ms ease",
              }}
            >
              <Paper
                withBorder
                p="md"
                radius="md"
                style={{ borderColor: theme.colors.error[2] }}
                bg="error.0"
              >
                <Group gap="sm" justify="center">
                  <AppIcon
                    iconName="Logout"
                    size={18}
                    color={theme.colors.error[6]}
                  />
                  <Text fw={600} size="md" c="error.6">
                    {t("navigation.logout")}
                  </Text>
                </Group>
              </Paper>
            </UnstyledButton>
          </Box>
          )}

          {/* App Version Footer */}
          <Stack align="center" gap={2} pt="lg" pb="md">
            <Text size="md" c="neutral.4">
              {brand.name} {appVersion}
            </Text>
            <Text size="md" c="neutral.4">
              © {new Date().getFullYear()} {brand.name}
            </Text>
          </Stack>
        </Stack>
      </ScrollArea>
    </>
  );
};